import React from 'react';
import Popup from 'reactjs-popup';
import { ColorRing } from 'react-loader-spinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import './Popups.scss';
import { StatusTypes } from 'api/traps/types';
import { Device } from 'api/InteractiveMap/types';
import { useTrapDeleteModalStore } from 'components/DeleteTrapModal/TrapsDeleteModal';

interface MCTTrapPopupProps {
  trap: Device;
  onView?: () => void;
  onMove?: () => void;
  showMoveActions?: boolean;
  onMoveAction?: (action: 'adjustment' | 'new_place', caught_pests?: number | null) => void;
  isMoving?: boolean;
  isLoading?: boolean;
  isActiveFilterArchive?: boolean;
}

export const MCTTrapPopup: React.FC<MCTTrapPopupProps> = ({
  trap,
  onMove,
  showMoveActions,
  onMoveAction,
  isLoading,
  isActiveFilterArchive,
}) => {
  const { openDeleteTrapModal } = useTrapDeleteModalStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const findLastTriggerWithoutCapture = () => {
    if (!trap.history_triggers) return null;

    const findAllTrippedCaught = trap.history_triggers.filter(trigger => trigger.status === StatusTypes.CAUGHT_EMPTY);
    if (!findAllTrippedCaught.length) return null;
    const lastTriggedWithoutCaptureDate = findAllTrippedCaught.sort(
      (a, b) => new Date(b.date_time).getTime() - new Date(a.date_time).getTime(),
    )[0]?.date_time;

    return lastTriggedWithoutCaptureDate;
  };
  const findLastTriggerCapture = () => {
    if (!trap.history_triggers) return null;

    const findAllCaptured = trap.history_triggers.filter(
      trigger => trigger.status === StatusTypes.APPROVED_MICE || trigger.status === StatusTypes.APPROVED_NOT_MICE,
    );
    if (!findAllCaptured.length) return null;
    const lastCaptureDate = findAllCaptured.sort(
      (a, b) => new Date(b.date_time).getTime() - new Date(a.date_time).getTime(),
    )[0]?.date_time;

    return lastCaptureDate;
  };

  const handleDeleteClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    openDeleteTrapModal(trap.id, trap.serial_number, true);
  };

  return (
    <div className="trap-popup mw-100" style={{ width: '500px' }}>
      <div className="trap-popup-header text-purple mb-2 fs-16">
        <strong>{t('Popups.MctPopup.title', { number: trap.number })}</strong>
      </div>
      <div>{t('Popups.MctPopup.serialNumber', { slug: trap.slug })}</div>
      {trap.history_triggers?.length ? (
        <div className="text-nowrap">
          {findLastTriggerCapture()
            ? t('Popups.MctPopup.lastCapture', {
                date: dayjs(findLastTriggerCapture()).format('DD.MM.YYYY HH:mm'),
              })
            : t('Popups.MctPopup.lastCapture', { date: '-' })}
        </div>
      ) : (
        <div className="text-success">{t('Popups.MctPopup.noCaptures')}</div>
      )}
      {trap.history_triggers?.length ? (
        <div className="text-nowrap">
          {findLastTriggerWithoutCapture()
            ? t('Popups.MctPopup.lastTriggedWithoutCapture', {
                date: dayjs(findLastTriggerWithoutCapture()).format('DD.MM.YYYY HH:mm'),
              })
            : t('Popups.MctPopup.lastTriggedWithoutCapture', { date: '-' })}
        </div>
      ) : (
        <div className="text-success">{t('Popups.MctPopup.noCaptures')}</div>
      )}
      {trap.last_maintenance ? (
        <div className="text-nowrap">
          {t('Popups.MctPopup.lastMaintenance', {
            date: dayjs(trap.last_maintenance).format('DD.MM.YYYY HH:mm'),
          })}
        </div>
      ) : (
        <div className="text-success">{t('Popups.MctPopup.noMaintenance' as any)}</div>
      )}
      <div className="d-flex align-content-center justify-content-between">
        {/* Installment comment with file icon */}
        {trap.installment_comment && (
          <Popup
            position="bottom left"
            trigger={
              <button className="btn btn-ghost-purple custom-toggle">
                <span className="icon-on">
                  <i className="ri-file-list-3-fill" />
                </span>
              </button>
            }
          >
            <div className="p-3 bg-white rounded-4">
              <strong>{t('Popups.MctPopup.installationComment')}</strong>
              <p className="text-muted fs-12 mt-2">{trap.installment_comment}</p>
            </div>
          </Popup>
        )}

        {/* Detail page with eye icon */}
        <div className="" title={t('Popups.MctPopup.viewTrapPage')}>
          <a href={`/r/trap/mct/info/${trap.slug}`} className="btn btn-ghost-info custom-toggle">
            <span className="icon-on">
              <i className="ri-eye-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-eye-fill align-bottom" />
            </span>
          </a>
        </div>

        {/* Move action with drag icon */}
        <div className="" title={t('Popups.MctPopup.moveTrap')}>
          <button
            type="button"
            className="btn btn-ghost-success custom-toggle"
            data-bs-toggle="button"
            onClick={onMove}
          >
            <span className="icon-on">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
          </button>
        </div>

        {/* Delete trap with trash icon */}
        <div className="" title={t('Popups.MctPopup.removeTrap')}>
          <button
            type="button"
            id="del_trap_btn"
            className="btn btn-ghost-danger custom-toggle"
            data-bs-toggle="button"
            onClick={handleDeleteClick}
          >
            <span className="icon-on">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
          </button>
        </div>
      </div>
      {/* Action End Block */}

      {/* Move Actions Row */}
      {showMoveActions && (
        <div className="move-actions-row mt-2 text-center">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80px' }}>
              <ColorRing
                visible
                height="50"
                width="50"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <div className="d-grid gap-4 mt-4">
              <div className="row row-cols-2 g-2 align-items-stretch">
                <div className="col-4">
                  <button className="move_buttons" onClick={() => onMoveAction && onMoveAction('adjustment')}>
                    {t('Popups.MctPopup.adjustment')}
                  </button>
                </div>

                <div className="col col-8 d-flex flex-column justify-content-center align-items-start">
                  <div className="fw-bold">{t('Popups.MctPopup.minorMove')}</div>
                  <span className="text-start">{t('Popups.MctPopup.adjustmentSubtitle')}</span>
                </div>
              </div>

              <div className="row g-2 align-items-stretch">
                <div className="col-4">
                  <button className="move_buttons" onClick={() => onMoveAction && onMoveAction('new_place')}>
                    {t('Popups.MctPopup.newPlace')}
                  </button>
                </div>

                <div className="col col-8 d-flex flex-column justify-content-start align-items-start">
                  <div className="fw-bold">{t('Popups.MctPopup.newLocation')}</div>
                  <span className="text-start">{t('Popups.MctPopup.newLocationSubtitle')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
